import React, { useState, useEffect } from 'react';
import {
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CButton,
  CContainer,
  CRow,
  CCol,
  CSpinner
} from '@coreui/react';
import { toast } from 'react-toastify';
import axios from 'axios';
import './AddObjects.scss';
import MainWindowObjects from './MainWindowObjects';

// Константа с base64 SVG иконкой по умолчанию
const DEFAULT_ICON_BASE64 = "data:image/svg+xml;base64," + btoa(`
<svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
  <rect width="64" height="64" rx="8" fill="#f0f0f0"/>
  <circle cx="32" cy="28" r="8" fill="#cccccc"/>
  <ellipse cx="32" cy="48" rx="12" ry="8" fill="#e0e0e0"/>
  <path d="M20 20L44 44M44 20L20 44" stroke="#cccccc" stroke-width="2"/>
</svg>
`);

const AddObjects = ({ visible, onClose, onObjectSelect, customerId, onObjectAdded }) => {
  const [objects, setObjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [mainWindowVisible, setMainWindowVisible] = useState(false);
  const [selectedObject, setSelectedObject] = useState(null);
  const token = localStorage.getItem("token");

  // Функция для загрузки объектов из API
  const fetchObjects = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await axios.get(`/api/neural_network_module/objects/`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (Array.isArray(response.data)) {
        setObjects(response.data);
      } else {
        setObjects([]);
        setError('Некорректный формат данных');
      }
    } catch (err) {
      setError('Не удалось загрузить список объектов: ' + (err.response?.data?.message || err.message));
      toast.error('Ошибка при загрузке объектов');
    } finally {
      setLoading(false);
    }
  };

  // Загружаем объекты при открытии модального окна
  useEffect(() => {
    if (visible) {
      fetchObjects();
    } else {
      setObjects([]);
      setMainWindowVisible(false);
      setSelectedObject(null);
    }
  }, [visible]);

  // Обработчик открытия главного окна
  const handleOpenMainWindow = (object) => {
    setSelectedObject(object);
    setMainWindowVisible(true);
  };

  // Строгое совпадение с "Здание"
  const handleObjectClick = (object) => {
    handleOpenMainWindow(object);
  };

  // Обработчик закрытия главного окна
  const handleCloseMainWindow = () => {
    setMainWindowVisible(false);
    setSelectedObject(null);
    if (onObjectAdded) {
      onObjectAdded();
    }
    onClose();
  };

  // Функция для отображения иконки или заглушки
  const renderIcon = (object) => {
    if (object.ObjectIcon && object.ObjectIcon !== 'no_icon' && object.ObjectIcon.trim() !== '') {
      let src = object.ObjectIcon;
      if (!src.startsWith('data:') && !src.startsWith('http')) {
        src = `data:image/png;base64,${object.ObjectIcon}`;
      }

      return (
        <img
          src={src}
          alt={object.ObjectName}
          className="object-icon-img"
          onError={(e) => {
            e.target.style.display = 'none';
            e.target.nextSibling.style.display = 'block';
          }}
        />
      );
    } else {
      return (
        <img
          src={DEFAULT_ICON_BASE64}
          alt="Default icon"
          className="object-icon-img"
        />
      );
    }
  };

  return (
    <>
      <CModal
        alignment="center"
        visible={visible}
        onClose={onClose}
        className="add-objects-modal"
        size="lg"
        backdrop="static"
        fullscreen="sm-down" // На мобилках будет на весь экран
      >
        <CModalHeader>
          <CModalTitle>Укажите тип объекта</CModalTitle>
        </CModalHeader>

        <CModalBody>
          {loading && (
            <div className="text-center py-4">
              <CSpinner color="primary" />
              <div className="mt-2">Загрузка объектов...</div>
            </div>
          )}

          {error && !loading && (
            <div className="text-center py-4">
              <div className="text-danger mb-2">{error}</div>
              <CButton
                color="primary"
                size="sm"
                onClick={fetchObjects}
              >
                Повторить попытку
              </CButton>
            </div>
          )}

          {!loading && !error && objects.length === 0 && (
            <div className="text-center py-4 text-muted">
              Нет доступных объектов
            </div>
          )}

          {!loading && !error && objects.length > 0 && (
            <CContainer fluid>
              {/* Используем g-3 для отступов сетки и убираем ручное разбиение на ряды */}
              <CRow className="g-3 justify-content-start">
                {objects.map((object) => {
                  return (
                    // На мобильном 2 в ряд (xs=6), на планшете 3 (sm=4), на ПК 4 (md=3)
                    <CCol key={object.ObjectID} xs={6} sm={4} md={3}>
                      <CButton
                        className="object-button d-flex flex-column align-items-center justify-content-center p-2 w-100"
                        variant="outline"
                        onClick={() => handleObjectClick(object)}
                      >
                        <div className="object-icon mb-2">
                          {renderIcon(object)}
                          <div className="default-icon-fallback" style={{ display: 'none' }}>
                            <img
                              src={DEFAULT_ICON_BASE64}
                              alt="Default icon"
                              className="object-icon-img"
                            />
                          </div>
                        </div>
                        <span className="object-name">{object.ObjectName}</span>
                      </CButton>
                    </CCol>
                  );
                })}
              </CRow>
            </CContainer>
          )}
        </CModalBody>
      </CModal>

      {/* Главное окно объекта */}
      <MainWindowObjects
        visible={mainWindowVisible}
        onClose={handleCloseMainWindow}
        availableObjectTypes={objects}
        selectedObjectType={selectedObject}
        customerId={customerId}
        onObjectAdded={onObjectAdded}
      />
    </>
  );
};

export default AddObjects;